<template>
    <div>
        <CollectionBanner :collection="collection" />

        <UiContentwrapper class="mt-8 md:mt-32">

            <UiFullcolumn>
                <div v-for="(products, type, index) in groupedProducts" :key="type" class="mb-16">

                    <h2>{{ type }}</h2>
                    <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 gap-8">
                        <ProductModel :model-value="product" v-for="product in products" :key="product.id" />
                    </div>
                </div>

            </UiFullcolumn>
        </UiContentwrapper>
        
        <CustompageBlogBasedLayout v-if="data.blog" :blog="data.blog" />

        <ContentFeaturedPages v-if="collection.featuredPages" :page="collection" />
    </div>
</template>

<script setup>

import { useProductStore } from '@/stores/productStore';
import { useBlogStore } from '@/stores/blogStore';
import { mapProductToGa4Item } from '@/utils/ga4Map';
const blogStore = useBlogStore();
const route = useRoute();
const productStore = useProductStore();
const { proxy } = useScriptGoogleTagManager({ id: 'GTM-TXPLJB8M' });


// Use a single useAsyncData to SSR all required data
const { data, error } = await useAsyncData(`shop-${route.params.slug}`, async () => {
    const [productRes, blogRes] = await Promise.all([
        productStore.fetchCollectionProductsByHandle(route.params.slug),
        blogStore.fetchBlogByHandle('PLP')
    ]);

    return {
        productRes,
        blog: blogRes
    };
});


const collection = computed(() => {
    return productStore.getCollectionByHandle(route.params.slug);
});

// Add SEO logic
if (collection.value) {
    useCollectionSeo(collection.value); // Call the SEO composable with the collection data
} else {
    console.warn(`Collection not found for handle: ${route.params.slug}`);
}

// Group products by type
const groupedProducts = computed(() => {
    const sortOrder = ['Road Wheelset', 'Gravel Wheelset', 'Wheelsets'];
    data.value.productRes.sort((a, b) => {
        const indexA = sortOrder.indexOf(a.productType);
        const indexB = sortOrder.indexOf(b.productType);
        if (indexA === -1 && indexB === -1) return 0;
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
    });
    return data.value.productRes.reduce((acc, product) => {
        const type = product.productType || 'unknown';
        if (!acc[type]) {
            acc[type] = [];
        }
        acc[type].push(product);
        return acc;
    }, {});
});

onMounted(async () => {

    console.log(`Pushing page_view for ${window.location.href} page to GTM dataLayer`);
    console.log('Collection title:', collection.value.title || '');
    proxy.dataLayer.push({
        event: 'page_view',
        pageType: 'PLP',
        pageUrl: window.location.href,
        pageTitle: collection.value.title || ''
    })

    // Track view_item_list for grouped products
    try {
        if (data.value?.productRes && data.value.productRes.length > 0) {
            const listId = `shop_${route.params.slug}`;
            const listName = window.location.pathname || `/shop/${route.params.slug}`;
            
            const items = data.value.productRes
                .map((p, i) =>
                    mapProductToGa4Item({
                        product: p,
                        index: i,
                        listId,
                        listName
                    })
                )
                .filter(Boolean);

            if (items.length > 0) {
                const currency =
                    data.value.productRes[0]?.priceRange?.minVariantPrice?.currencyCode ??
                    data.value.productRes[0]?.variants?.edges?.[0]?.node?.price?.currencyCode ??
                    'EUR';

                console.log('Pushing view_item_list to dataLayer', {
                    currency,
                    items
                });

                proxy.dataLayer.push({
                    event: 'view_item_list',
                    ecommerce: {
                        currency,
                        items
                    },
                    pageUrl: window.location.href
                });
            }
        }
    } catch (err) {
        console.warn('Error while pushing view_item_list analytics', err);
    }

});


</script>